<template>
  <SmartLinkItemComponent :id="model.system.id" :component="!model.system.workflow">
    <div class="document-list">
      <div class="container">
        <SmartLinkStandaloneElement :codename="typ.elements.title.codename">
          <h2 class="title" v-if="data.title">
            <font-awesome class="icon" :icon="data.icon" v-if="data.icon" />
            {{ data.title }}
          </h2>
        </SmartLinkStandaloneElement>
        <SmartLinkStandaloneElement :codename="typ.elements.content.codename">
          <div class="content">
            <RichText :value="data.content" />
          </div>
        </SmartLinkStandaloneElement>
        <!-- Table -->
        <SmartLinkStandaloneElement :codename="typ.elements.documents.codename">
          <table class="table document-table">
            <thead>
              <tr>
                <th :colspan="hasAnyDate ? 1 : 2">{{$t('title')}}</th>
                <th v-if="hasAnyDate" colspan="2">{{$t('date')}}</th>
              </tr>
            </thead>
            <tbody>
              <DocumentListItem
                v-for="doc in documentList"
                :model="doc"
                :has-any-date="hasAnyDate"
              />
            </tbody>
          </table>
        </SmartLinkStandaloneElement>
        <!-- Table Pagination -->
        <nav class="document-pagination" v-if="pageCount > 1">
          <ul class="pagination">
            <li class="page-item">
              <a class="page-link" @click="previousPage" :aria-disabled="currentPage <= 1"
                >{{$t('previous')}}
              </a>
            </li>
            <li class="page-item" v-for="i in getPageList()">
              <a class="page-link" :class="{ active: currentPage == i }" @click="pageClicked(i)">{{
                i
              }}</a>
            </li>
            <li class="page-item">
              <a class="page-link" @click="nextPage" :aria-disabled="currentPage >= pageCount"
                >{{$t('next')}}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  </SmartLinkItemComponent>
</template>

<script setup lang="ts">
import type { DocumentListModel, DocumentModel } from '~/models'
import { contentTypes } from '~/models'
const typ = contentTypes.document_list

const pageStore = usePageStore()
const props = defineProps<{ model: DocumentListModel }>()

const documentList: Ref<DocumentModel[]> = ref([])
const data = {
  title: props.model.elements.title.value,
  icon: firstOrDefault(props.model.elements.icon.value),
  content: props.model.elements.content.value,
  documents: pageStore.getListAs<DocumentModel>(props.model.elements.documents.value)
}

const hasAnyDate = computed(() => {
  return data.documents.some((doc) => {
    const hasParentDate = !!doc.elements.date.value
    const hasChildDate = pageStore
      .getListAs<DocumentModel>(doc.elements.childItems.value)
      .some((child) => !!child.elements.date.value)

    return hasParentDate || hasChildDate
  })
})

const dataCount = data.documents.length
const itemsPerPage = 15
const currentPage = ref(1)

let pageCount = dataCount / itemsPerPage > 0 ? dataCount / itemsPerPage : 1
if (pageCount > parseInt(pageCount.toString())) {
  pageCount = parseInt((pageCount + 1).toString())
}

setDataByPage()
function previousPage() {
  if (1 <= currentPage.value - 1) {
    currentPage.value = currentPage.value - 1
    setDataByPage()
  }
}
function nextPage() {
  if (pageCount >= currentPage.value + 1) {
    currentPage.value = currentPage.value + 1
    setDataByPage()
  }
}

function pageClicked(pageNumber: number) {
  currentPage.value = pageNumber
  setDataByPage()
}
async function setDataByPage() {
  const initialIndex = (currentPage.value - 1) * itemsPerPage
  let endIndex = currentPage.value * itemsPerPage

  if (endIndex > data.documents.length) {
    endIndex = data.documents.length
  }

  documentList.value = []

  for (let i = initialIndex; i < endIndex; i++) {
    documentList.value.push(data.documents[i])
  }
}

function getPageList() {
  const result: number[] = []

  for (let i = 1; i <= pageCount; i++) {
    result.push(i)
  }

  return result
}
</script>
